import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import Modal from '../common/Modal';
import toast from 'react-hot-toast';
import { Customer, CustomerRelationshipLog } from '../../types/customer';
import { ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

interface EditRelationshipLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
  logToEdit: CustomerRelationshipLog | null;
  customer: Customer;
}

const EditRelationshipLogModal: React.FC<EditRelationshipLogModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  logToEdit,
  customer
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [content, setContent] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isOpenTask, setIsOpenTask] = useState<boolean>(false);

  // Reset form and populate fields when modal opens or logToEdit changes
  useEffect(() => {
    if (isOpen && logToEdit) {
      setContent(logToEdit.content);
      setIsOpenTask(logToEdit.isOpen);
      setError('');
    }
  }, [isOpen, logToEdit]);

  const maxLength = 1000;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!content.trim()) {
      setError(t('customers.relationshipLog.contentRequired') || 'Content is required');
      return;
    }
    
    if (content.length > maxLength) {
      setError(t('customers.relationshipLog.maxLength') || `Maximum ${maxLength} characters`);
      return;
    }

    if (!user || !logToEdit) {
      setError(t('common.messages.authContext.error') || 'Authentication error');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Make sure we use the correct customer ID from the logToEdit
      const customerId = logToEdit.customerId;
      const customerRef = doc(db, 'customers', customerId);
      
      // Get the current customer document to work with the latest data
      const customerDocRef = doc(db, 'customers', customerId);
      const customerSnapshot = await getDoc(customerDocRef);
      if (!customerSnapshot.exists) {
        throw new Error('Customer not found');
      }
      
      const customerData = customerSnapshot.data() as Customer;
      const currentLogs = customerData.relationshipLogs || [];
      
      // Find the index of the log we're editing
      const logIndex = currentLogs.findIndex(log => log.id === logToEdit.id);
      if (logIndex === -1) {
        throw new Error('Log entry not found in customer data');
      }
      
      // Create a new logs array with the updated log
      const updatedLogs = [...currentLogs];
      updatedLogs[logIndex] = {
        ...currentLogs[logIndex],
        content, // Update the content
        isOpen: isOpenTask // Update the isOpen status
      };
      
      // Update the entire logs array at once
      await updateDoc(customerRef, {
        relationshipLogs: updatedLogs
      });
      
      toast.success(t('customers.relationshipLog.editSuccess') || 'Log entry updated successfully');
      
      // Reset form and close modal
      setContent('');
      await onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to update log entry:', err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      toast.error(
        `${t('customers.relationshipLog.editError') || 'Failed to update log entry'}: ${errorMessage}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('customers.relationshipLog.edit') || 'Edit log entry'}
      size="xl"
    >
      <div className="p-6">
        <form onSubmit={handleSubmit}>
          {/* Entry ID display */}
          {logToEdit && (
            <div className="mb-6">
              <label className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('customers.relationshipLog.entryId') || 'Entry ID'}
              </label>
              <div className="text-base bg-gray-100 dark:bg-gray-700 p-3 rounded font-mono text-gray-900 dark:text-gray-200">
                {logToEdit.entryId}
              </div>
            </div>
          )}
          
          {/* Content textarea */}
          <div className="mb-6">
            <label htmlFor="log-content" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('customers.relationshipLog.content') || 'Content'} <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <textarea
              id="log-content"
              rows={8}
              className={`w-full rounded-lg border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 ${
                error ? 'border-red-300 dark:border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500'
              } shadow-sm p-4 text-base`}
              placeholder={t('customers.relationshipLog.contentPlaceholder') || 'Enter log content...'}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={maxLength}
              disabled={isSubmitting}
            />
            <div className="flex justify-between mt-2">
              {error ? (
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('customers.relationshipLog.maxLength') || `Maximum ${maxLength} characters`}
                </p>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {content.length}/{maxLength}
              </p>
            </div>
          </div>
          
          {/* isOpen checkbox */}
          <div className="mb-6">
            <div className="flex items-center">
              <input
                id="isOpenTask"
                name="isOpenTask"
                type="checkbox"
                className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                checked={isOpenTask}
                onChange={(e) => setIsOpenTask(e.target.checked)}
                disabled={isSubmitting}
              />
              <label htmlFor="isOpenTask" className="ml-3 flex items-center text-base font-medium text-gray-700 dark:text-gray-300">
                {isOpenTask ? (
                  <>
                    <ExclamationCircleIcon className="h-5 w-5 text-amber-500 dark:text-amber-400 mr-2" />
                    {t('customers.relationshipLog.hasOpenTasks') || 'Has pending tasks'}
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-5 w-5 text-green-500 dark:text-green-400 mr-2" />
                    {t('customers.relationshipLog.noOpenTasks') || 'No pending tasks'}
                  </>
                )}
              </label>
            </div>
            <p className="mt-1 ml-8 text-sm text-gray-500 dark:text-gray-400">
              {isOpenTask 
                ? t('customers.relationshipLog.pendingTaskDescription') || 'There are still pending tasks for this log entry'
                : t('customers.relationshipLog.completedTaskDescription') || 'All tasks for this log entry are complete'}
            </p>
          </div>
          
          {/* Actions */}
          <div className="flex justify-end space-x-4 mt-8">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-base font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={isSubmitting}
            >
              {t('customers.relationshipLog.cancel') || 'Cancel'}
            </button>
            <button
              type="submit"
              className="px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? t('common.submitting') || 'Saving...'
                : t('customers.relationshipLog.update') || 'Update entry'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EditRelationshipLogModal; 